import {
	Camera,
	Quaternion,
	Vector2,
	Vector3,
} from "three";

interface WStrackballControlsScreen {
	left: number;
	top: number;
	width: number;
	height: number;
}

enum State {
	none = -1,
	rotate = 0,
	zoom = 1,
	pan = 2,
	touchRotate = 3,
	touchZoomPan = 4
}

export class WStrackballControls
{
	camera: Camera;
	domElement: Node;
	target: Vector3;
	distance: number;
	quaternion: Quaternion;
	enabled: boolean;
	screen: WStrackballControlsScreen;

	rotationalSpeed: number;
	panSpeed: number;
	zoomSpeed: number;

	screenPosPrev: Vector2;
	screenPosCurr: Vector2;

	touchZoomLengthSqPrev: number;
	touchZoomLengthSqCurr: number;

	state: State;

	mousemoveListener: EventListener;
	mouseupListener: EventListener;

	constructor(camera: Camera, domElement?: Node)
	{
		// Underlying camera that is modified to apply control requests.
		// When moving the camera its position and rotation will be modified.
		this.camera = camera;

		// Required when resizing
		this.domElement = (domElement !== undefined) ? domElement : document;

		// API - variables

		// Rotational center of the camera; modified when panning
		this.target = new Vector3();

		// Distance of the camera from the object; modified when zooming
		this.distance = 1000.;

		// Rotation of the camera
		this.quaternion = new Quaternion();

		// API - parameters

		// If false input events will be ignored
		this.enabled = true;

		this.screen = {left: 0, top: 0, width: 0, height: 0};

		this.rotationalSpeed = 3.;
		this.panSpeed = 1.;
		this.zoomSpeed = 2.;

		// API - methods -- see below

		// Internal - variables

		this.screenPosPrev = new Vector2();
		this.screenPosCurr = new Vector2();

		this.touchZoomLengthSqPrev = 0.;
		this.touchZoomLengthSqCurr = 0.;

		this.state = State.none;

		// Internal - methods

		// Setup
		this.handleResize();

		this.mousemoveListener = (event) => { this.mousemove(event); };
		this.mouseupListener = (event) => { this.mouseup(event); };

		this.domElement.addEventListener("mousedown", (event) => this.mousedown(event), false);
		this.domElement.addEventListener("wheel", (event) => this.mousewheel(event), false);

		this.domElement.addEventListener("touchstart", (event) => this.touchstart(event), false);
		// Will not be removed since touch end might be called for e. g. a transition from pan to rotate
		// or three-finger-pan to two-finger-pan => event listener is required after touchend in these cases
		this.domElement.addEventListener("touchmove", (event) => this.touchmove(event), false);
		// Will not be removed since touch end might be called for e. g. a transition from pan to rotate
		// or three-finger-pan to two-finger-pan => event listener is required after touchend in these cases
		this.domElement.addEventListener("touchend", (event) => this.touchend(event), false);

		this.domElement.addEventListener("contextmenu", (event) => this.contextmenu(event), false);
	}

	screenPosDelta()
	{
		const v = new Vector3(this.screenPosCurr.x - this.screenPosPrev.x, this.screenPosCurr.y - this.screenPosPrev.y, 0);
		return v;
	}

	rotate()
	{
		const dragVector = this.screenPosDelta();
		this.screenPosPrev.copy(this.screenPosCurr);

		const angle = dragVector.length() * this.rotationalSpeed * -1.;

		if (angle === 0) {
			// Nothing to do
			return;
		}

		// Axis should be perpendicular to drag direction
		const axis = new Vector3(dragVector.y * -1, dragVector.x, 0.).normalize();

		const rotationDeltaQ = new Quaternion().setFromAxisAngle(axis, angle);

		this.quaternion.multiply(rotationDeltaQ);
	}

	pan()
	{
		const delta = this.screenPosDelta();
		this.target.add(new Vector3(-delta.x, delta.y, delta.z).applyQuaternion(this.quaternion).multiplyScalar(this.distance * this.panSpeed));
		this.screenPosPrev.copy(this.screenPosCurr);
	}

	zoom()
	{
		let factor = 1.;
		if (this.state === State.touchZoomPan) {
			factor = this.touchZoomLengthSqPrev / this.touchZoomLengthSqCurr;
			this.touchZoomLengthSqPrev = this.touchZoomLengthSqCurr;
		} else {
			factor = 1. + this.screenPosDelta().y * this.zoomSpeed;
		}

		this.screenPosPrev.copy(this.screenPosCurr);
		this.distance *= factor;
	}

	processUserInput()
	{
		if (!this.enabled) {
			return;
		}

		switch (this.state) {
			case State.rotate:
			case State.touchRotate: this.rotate(); break;

			case State.pan: this.pan(); break;
			case State.zoom: this.zoom(); break;
			case State.touchZoomPan:
				this.pan();
				this.zoom();
				break;

			default:
				// Unhandled state so nothing to do
				return;
		}

		this.update();
	}

	// Cf. THREE.TrackballControls
	getMouseOnScreen(pageX: number, pageY: number)
	{
		const vector = new Vector2();
		vector.set((pageX - this.screen.left) / this.screen.width, (pageY - this.screen.top) / this.screen.height);
		return vector;
	}

	// Cf. THREE.TrackballControls
	getMouseOnCircle(pageX: number, pageY: number)
	{
		const vector = new Vector2();
		vector.set(
		        ((pageX - this.screen.width * 0.5 - this.screen.left) / (this.screen.width * 0.5)),
		        ((this.screen.height + 2 * (this.screen.top - pageY)) / this.screen.width) // screen.width intentional
		);
		return vector;
	}

	// API - methods
	update()
	{
		// Set camera according to target, distance and quaternions
		const targetToCamera = new Vector3(0., 0., this.distance).applyQuaternion(this.quaternion);

		const cameraPosition = this.target.clone().add(targetToCamera);

		this.camera.position.copy(cameraPosition);
		this.camera.setRotationFromQuaternion(this.quaternion);
	}

	// Cf. THREE.TrackballControls
	handleResize()
	{
		if (this.domElement === document) {
			this.screen.left = 0;
			this.screen.top = 0;
			this.screen.width = window.innerWidth;
			this.screen.height = window.innerHeight;
		} else {
			if (!(this.domElement instanceof HTMLElement)) {
				throw new Error("domElement should be a HTMLElement");
			}

			const box = this.domElement.getBoundingClientRect();
			// adjustments come from similar code in the jquery offset() function
			if (this.domElement.ownerDocument === null) {
				throw new Error("domElement.ownerDocument === null");
			}

			const d = this.domElement.ownerDocument.documentElement;
			this.screen.left = box.left + window.pageXOffset - d.clientLeft;
			this.screen.top = box.top + window.pageYOffset - d.clientTop;
			this.screen.width = box.width;
			this.screen.height = box.height;
		}
	}

	setSize(left: number, top: number, width: number, height: number)
	{
		this.screen.left = left;
		this.screen.top = top;
		this.screen.width = width;
		this.screen.height = height;
	}

	// Internal - event callbacks

	// Cf. THREE.TrackballControls
	mousedown(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof MouseEvent)) {
			throw new Error("event must be of type MouseEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		if (this.state === State.none) {
			this.state = event.button;
		}

		if (this.state === State.rotate) {
			this.screenPosCurr.copy(this.getMouseOnCircle(event.pageX, event.pageY));
			this.screenPosPrev.copy(this.screenPosCurr);
		} else {
			this.screenPosCurr.copy(this.getMouseOnScreen(event.pageX, event.pageY));
			this.screenPosPrev.copy(this.screenPosCurr);
		}

		document.addEventListener("mousemove", this.mousemoveListener, false);
		document.addEventListener("mouseup", this.mouseupListener, false);

		this.processUserInput();
	}

	// Cf. THREE.TrackballControls
	mousemove(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof MouseEvent)) {
			throw new Error("event must be of type MouseEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		if (this.state === State.rotate) {
			this.screenPosCurr.copy(this.getMouseOnCircle(event.pageX, event.pageY));
		} else {
			this.screenPosCurr.copy(this.getMouseOnScreen(event.pageX, event.pageY));
		}

		this.processUserInput();
	}

	// Cf. THREE.TrackballControls
	mouseup(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof MouseEvent)) {
			throw new Error("event must be of type MouseEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		this.state = State.none;

		document.removeEventListener("mousemove", this.mousemoveListener);
		document.removeEventListener("mouseup", this.mouseupListener);
	}

	// Cf. THREE.TrackballControls
	mousewheel(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof WheelEvent)) {
			throw new Error("event must be of type WheelEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		switch (event.deltaMode) {
			case 2:
				// Zoom in pages
				this.screenPosCurr.y += event.deltaY * 0.025;
				break;
			case 1:
				// Zoom in lines
				this.screenPosCurr.y += event.deltaY * 0.01;
				break;
			default:
				// undefined, 0, assume pixels
				this.screenPosCurr.y += event.deltaY * 0.00025;
				break;
		}

		this.state = State.zoom;
		this.processUserInput();
		this.state = State.none;
	}

	// Cf. THREE.TrackballControls
	touchstart(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof TouchEvent)) {
			throw new Error("event most be of type TouchEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		if (event.touches.length === 1) {
			this.state = State.touchRotate;
			this.screenPosCurr.copy(this.getMouseOnCircle(event.touches[0].pageX, event.touches[0].pageY));
			this.screenPosPrev.copy(this.screenPosCurr);
		} else {
			// Two or more touches

			this.state = State.touchZoomPan;
			const dx = event.touches[0].pageX - event.touches[1].pageX;
			const dy = event.touches[0].pageY - event.touches[1].pageY;
			this.touchZoomLengthSqCurr = this.touchZoomLengthSqPrev = dx * dx + dy * dy;

			const x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
			const y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
			this.screenPosCurr.copy(this.getMouseOnScreen(x, y));
			this.screenPosPrev.copy(this.screenPosCurr);
		}
		this.processUserInput();
	}

	touchmove(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof TouchEvent)) {
			throw new Error("event most be of type TouchEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		if (event.touches.length === 1) {
			this.screenPosPrev.copy(this.screenPosCurr);
			this.screenPosCurr.copy(this.getMouseOnCircle(event.touches[0].pageX, event.touches[0].pageY));
		} else {
			const dx = event.touches[0].pageX - event.touches[1].pageX;
			const dy = event.touches[0].pageY - event.touches[1].pageY;
			this.touchZoomLengthSqCurr = dx * dx + dy * dy;

			const x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
			const y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
			this.screenPosCurr.copy(this.getMouseOnScreen(x, y));
		}
		this.processUserInput();
	}

	touchend(event: Event)
	{
		if (this.enabled === false) {
			return;
		}

		if (!(event instanceof TouchEvent)) {
			throw new Error("event most be of type TouchEvent");
		}

		event.preventDefault();
		event.stopPropagation();

		if (event.touches.length === 0) {
			this.state = State.none;
		} else if (event.touches.length === 1) {
			this.state = State.touchRotate;
			this.screenPosCurr.copy(this.getMouseOnCircle(event.touches[0].pageX, event.touches[0].pageY));
			this.screenPosPrev.copy(this.screenPosCurr);
		}
		this.processUserInput();
	}

	contextmenu(event: Event)
	{
		if (this.enabled === false) {
			return;
		}
		event.preventDefault();
	}
}
